import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export const DeletionLogCard = () => {
  const [entries, setEntries] = useState(null);
  const [limit, setLimit] = useState(50);
  const load = (n = limit) => axios.get(`${API}/invoices/deletion-log`, { params: { limit: n } }).then((r) => setEntries(r.data.entries)).catch(() => setEntries([]));
  useEffect(() => { load(limit); }, [limit]); // eslint-disable-line
  return (
    <Card className="bg-white border-0 shadow-lg mt-6" data-testid="deletion-log-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base md:text-lg"><Trash2 className="w-5 h-5 text-red-600" /> Invoice deletion log</CardTitle>
            <CardDescription>Who removed which invoice or series, and when. Invoices with recorded payments can never be deleted.</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => load()} data-testid="deletion-log-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        {entries === null ? <p className="text-sm text-slate-400">Loading…</p> : entries.length === 0 ? <p className="text-sm text-slate-400" data-testid="deletion-log-empty">Nothing has been deleted yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-slate-500 border-b"><th className="py-2 pr-3">When</th><th className="py-2 pr-3">By</th><th className="py-2 pr-3">What</th><th className="py-2 pr-3">Customer</th><th className="py-2 pr-3 text-right">Amount</th></tr></thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100" data-testid="deletion-log-row">
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{when(e.at)}</td>
                    <td className="py-2 pr-3 font-medium text-slate-800">{e.by}</td>
                    <td className="py-2 pr-3">{e.type === "series" ? <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Series stopped & removed</Badge> : <span>Invoice <span className="font-medium">{e.invoice_number}</span>{e.paid_override ? <Badge className="ml-1 bg-amber-100 text-amber-800 hover:bg-amber-100">had payments</Badge> : null}</span>}</td>
                    <td className="py-2 pr-3 text-slate-600">{e.customer_name || "—"}</td>
                    <td className="py-2 pr-3 text-right text-slate-700">{e.type === "series" ? "—" : money(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length >= limit && <Button variant="ghost" size="sm" className="mt-2" onClick={() => setLimit(limit + 100)} data-testid="deletion-log-more">Show more</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
